import React, { useState, useEffect } from 'react';
import { Typography, Container, Box, Card, CardContent, useTheme, Divider, Button, Popover } from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import apiService from '../utils/api';

const Experience = () => {
  const theme = useTheme();
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);

  // 处理Popover打开
  const handlePopoverOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  // 处理Popover关闭
  const handlePopoverClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);

  useEffect(() => {
    const fetchArticles = async () => {
      try {
        setLoading(true);
        // 使用API服务获取热门文章
        const data = await apiService.getHottestArticles();
        setArticles(data);
        setLoading(false);
      } catch (err) {
        console.error("Error fetching articles:", err);
        setError(err.message);
        setLoading(false);
      }
    };

    fetchArticles();
  }, []);
  
  return (
    <Box 
      sx={{ 
        background: theme.palette.secondary.light,
        py: { xs: 8, md: 12 }
      }}
      id="experience"
    >
      <Container maxWidth="lg">
        <Box className="text-center mb-16">
          <Typography 
            variant="h2" 
            className="text-3xl sm:text-4xl"
            sx={{
              color: theme.palette.text.primary,
              fontWeight: 600,
              mb: 3
            }}
          >
            我的观察
          </Typography>
          
          <Typography
            variant="body1"
            className="max-w-2xl mx-auto"
            sx={{
              color: theme.palette.text.secondary,
              fontSize: '1.125rem',
              lineHeight: 1.6,
              mb: 8,
              textAlign: 'center',
              margin: '0 auto',
              maxWidth: '600px'
            }}
          >
            看到看不到的地方
          </Typography>
        </Box>
        
        {loading ? (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <Typography>加载中...</Typography>
          </Box>
        ) : error ? (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <Typography color="error">加载失败: {error}</Typography>
          </Box>
        ) : (
          <>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 4 }}>
              {articles.map((article) => (
                <Box key={article.id} sx={{ width: '340px' }}>
                  <Card 
                    elevation={0} 
                    className="h-full transition-all duration-300 hover:translate-y-[-8px]"
                    sx={{
                      height: '100%',
                      width: '100%',
                      background: '#ffffff',
                      border: '1px solid rgba(0,0,0,0.05)',
                      borderRadius: '16px',
                      overflow: 'hidden',
                      transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                      willChange: 'transform',
                      '&:hover': {
                        boxShadow: '0 10px 30px rgba(0,0,0,0.05)',
                        transform: 'translateY(-8px)'
                      }
                    }}
                  >
                    <Box 
                      sx={{
                        height: 150,
                        background: article.coverImg 
                          ? `linear-gradient(135deg, rgba(77, 124, 111, 0.85) 0%, rgba(44, 90, 77, 0.9) 100%), url(${article.coverImg})`
                          : `linear-gradient(135deg, ${theme.palette.primary.light} 0%, ${theme.palette.primary.dark} 100%)`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        position: 'relative',
                        overflow: 'hidden'
                      }}
                    >
                      <Box 
                        sx={{ 
                          p: 3, 
                          display: 'flex',
                          flexDirection: 'column',
                          height: '100%',
                          justifyContent: 'space-between',
                          position: 'relative'
                        }}
                      >
                        <Box>
                          <Typography 
                            variant="caption" 
                            sx={{ 
                              color: 'rgba(255,255,255,0.8)',
                              display: 'block',
                              mb: 1,
                              fontSize: '0.75rem'
                            }}
                          >
                            {article.date} · {article.readTime}阅读
                          </Typography>

                          <Typography 
                            variant="h5" 
                            sx={{
                              color: '#fff',
                              fontWeight: 600,
                              fontSize: '1.35rem',
                              lineHeight: 1.3,
                              mb: 1
                            }}
                          >
                            {article.title}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                    
                    <CardContent 
                      sx={{ 
                        p: 3,
                        '&:last-child': { pb: 3 }
                      }}
                    >
                      <Typography 
                        variant="body2" 
                        sx={{
                          color: theme.palette.text.secondary,
                          lineHeight: 1.6,
                          fontSize: '0.925rem',
                          mb: 3,
                          overflow: 'hidden',
                          display: '-webkit-box',
                          WebkitLineClamp: 3,
                          WebkitBoxOrient: 'vertical',
                          textOverflow: 'ellipsis',
                          height: 70
                        }}
                      >
                        {article.excerpt}
                      </Typography>
                      
                      <Divider sx={{ my: 2, opacity: 0.1 }} />
                      
                      <Box 
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                        }}
                      >
                        <Box 
                          sx={{ 
                            display: 'flex',
                            alignItems: 'center',
                            color: theme.palette.text.secondary
                          }}
                        >
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M12 21.35L10.55 20.03C5.4 15.36 2 12.27 2 8.5C2 5.41 4.42 3 7.5 3C9.24 3 10.91 3.81 12 5.08C13.09 3.81 14.76 3 16.5 3C19.58 3 22 5.41 22 8.5C22 12.27 18.6 15.36 13.45 20.03L12 21.35Z" fill="currentColor" opacity="0.6"/>
                          </svg>
                          <Typography variant="body2" sx={{ ml: 1, fontSize: '0.875rem' }}>
                            {article.likes}
                          </Typography>
                        </Box>
                        
                        <Button 
                          variant="text" 
                          size="small"
                          component="a"
                          href={article.link}
                          target="_blank"
                          rel="noopener noreferrer"
                          endIcon={<ArrowForwardIcon fontSize="small" />}
                          sx={{ 
                            color: theme.palette.primary.main,
                            fontWeight: 500,
                            textTransform: 'none',
                            '&:hover': {
                              background: 'transparent',
                              color: theme.palette.primary.dark,
                            }
                          }}
                        >
                          阅读全文
                        </Button>
                      </Box>
                    </CardContent>
                  </Card>
                </Box>
              ))}
            </Box>
            
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 6, mr: { xs: 2, md: 8 } }}>
              <Button 
                aria-owns={open ? 'mouse-over-popover' : undefined}
                aria-haspopup="true"
                onMouseEnter={handlePopoverOpen}
                onMouseLeave={handlePopoverClose}
                variant="outlined" 
                color="primary"
                component="a"
                endIcon={<ArrowForwardIcon />}
                sx={{ 
                  textTransform: 'none',
                  borderRadius: '30px',
                  px: 3,
                  py: 1,
                  borderColor: theme.palette.primary.main,
                  '&:hover': {
                    borderColor: theme.palette.primary.dark,
                    background: 'rgba(77, 124, 111, 0.04)',
                  }
                }}
              >
                阅读更多
              </Button>
              
              <Popover
                id="mouse-over-popover"
                sx={{
                  pointerEvents: 'none',
                }}
                open={open}
                anchorEl={anchorEl}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'center',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'center',
                }}
                onClose={handlePopoverClose}
                disableRestoreFocus
              >
                <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>
                    关注微信公众号获取更多内容
                  </Typography>
                  <img src="/images/mp-qrcode.png" alt="微信公众号二维码" style={{ width: 180, height: 180 }} />
                </Box>
              </Popover>
            </Box>
          </>
        )}
      </Container>
    </Box>
  );
};

export default Experience; 